import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, ClipboardCheck, Eye, X } from 'lucide-react';

const INSPECTION_METHODS = [
  { value: 'APPEARANCE', label: 'Appearance' },
  { value: 'PACKING', label: 'Packing' },
  { value: 'COLOUR', label: 'Colour' },
  { value: 'MELT_FLOW_INDEX', label: 'Melt Flow Index' },
  { value: 'MOISTURE_CONTENT', label: 'Moisture Content' },
  { value: 'TEST_CERTIFICATE', label: 'Test Certificate' },
];

const INSPECTION_RESULTS = [
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'HOLD', label: 'Hold' },
  { value: 'REJECTED', label: 'Rejected' },
];

const IncomingInspection = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [inspectedItemIds, setInspectedItemIds] = useState([]);
  
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [methodDetails, setMethodDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // View dialog
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingInspection, setViewingInspection] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'QUALITY', 'RM_STORE']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/incoming-inspections');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to fetch inspections');
    } finally {
      setLoading(false);
    }
    try { const r = await api.get('/purchases'); setPurchases(r.data); } catch (e) { console.error('purchases fetch failed', e); }
    try { const r = await api.get('/employees'); setEmployees(r.data); } catch (e) { console.error('employees fetch failed', e); }
    try { const r = await api.get('/items'); setItems(r.data); } catch (e) { console.error('items fetch failed', e); }
    try { const r = await api.get('/stores'); setStores(r.data); } catch (e) { console.error('stores fetch failed', e); }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch items when GRN changes
  const fetchPurchaseItems = async (purchaseId) => {
    if (!purchaseId) {
      setPurchaseItems([]);
      return;
    }
    try {
      const response = await api.get(`/purchases/${purchaseId}/items`);
      setPurchaseItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase items');
      setPurchaseItems([]);
    }
  };

  const getPurchaseLabel = (purchaseId) => {
    const purchase = purchases.find(p => p.id === purchaseId);
    return purchase ? `GRN-${purchase.grn_no}` : `ID: ${purchaseId}`;
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.code} - ${item.name}` : `ID: ${itemId}`;
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.employee_code} - ${emp.first_name} ${emp.last_name || ''}` : `ID: ${employeeId}`;
  };

  const getMethodLabel = (method) => {
    const found = INSPECTION_METHODS.find(function(m) { return m.value === method; });
    return found ? found.label : method;
  };

  const getStoreName = (storeId) => {
    if (!storeId) return '-';
    var s = stores.find(function(st) { return st.id === parseInt(storeId) || st.name === storeId; });
    return s ? s.name : storeId;
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        purchase_id: (item.purchase_id ? item.purchase_id.toString() : ''),
        item_id: (item.item_id ? item.item_id.toString() : ''),
        inspection_date: item.inspection_date || '',
        employee_id: (item.employee_id ? item.employee_id.toString() : ''),
        inspection_result: item.inspection_result || '',
        moved_to_location: item.moved_to_location || '',
        remarks: item.remarks || '',
      });
      setMethodDetails(item.method_details || []);
      // Fetch items for this purchase
      fetchPurchaseItems(item.purchase_id);
    } else {
      setEditingItem(null);
      setFormData({
        purchase_id: '',
        item_id: '',
        inspection_date: new Date().toISOString().split('T')[0],
        employee_id: '',
        inspection_result: '',
        moved_to_location: '',
        remarks: '',
      });
      setMethodDetails([]);
      setPurchaseItems([]);
    }
    setIsDialogOpen(true);
  };

  const handlePurchaseChange = (purchaseId) => {
    setFormData(prev => ({ ...prev, purchase_id: purchaseId, item_id: '' }));
    fetchPurchaseItems(parseInt(purchaseId));
    // Fetch already inspected items for this purchase
    api.get('/purchases/' + purchaseId + '/inspected-items').then(function(res) {
      setInspectedItemIds(res.data);
    }).catch(function() { setInspectedItemIds([]); });
  };

  const handleAddMethod = () => {
    setMethodDetails([...methodDetails, { method: '', value: '' }]);
  };

  const handleMethodChange = (index, field, value) => {
    const updated = [...methodDetails];
    updated[index] = { ...updated[index], [field]: value };
    setMethodDetails(updated);
  };

  const handleRemoveMethod = (index) => {
    setMethodDetails(methodDetails.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.purchase_id || !formData.item_id || !formData.employee_id || !formData.inspection_result) {
      toast.error('GRN, Item, Employee and Result are required');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        purchase_id: parseInt(formData.purchase_id),
        item_id: parseInt(formData.item_id),
        inspection_date: formData.inspection_date,
        employee_id: parseInt(formData.employee_id),
        inspection_result: formData.inspection_result,
        moved_to_location: formData.moved_to_location || null,
        remarks: formData.remarks || null,
        method_details: methodDetails.filter(m => m.method).map(m => ({
          method: m.method,
          value: m.value || ''
        })),
      };

      if (editingItem) {
        await api.put(`/incoming-inspections/${editingItem.id}`, submitData);
        toast.success('Inspection updated');
      } else {
        await api.post('/incoming-inspections', submitData);
        toast.success('Inspection created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this inspection?')) return;
    try {
      await api.delete(`/incoming-inspections/${item.id}`);
      toast.success('Inspection deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const response = await api.get(`/incoming-inspections/${item.id}`);
      setViewingInspection(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load inspection details');
    }
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const purchaseLabel = getPurchaseLabel(item.purchase_id).toLowerCase();
    const itemName = getItemName(item.item_id).toLowerCase();
    return purchaseLabel.includes(term) || itemName.includes(term) || (item.inspection_result || '').toLowerCase().includes(term);
  });

  const getResultBadge = (result) => {
    switch (result) {
      case 'ACCEPTED':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Accepted</span>;
      case 'HOLD':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Hold</span>;
      case 'REJECTED':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{result}</span>;
    }
  };

  // Filter purchases to show only those waiting for inspection
  const pendingPurchases = purchases.filter(function(p) {
    return p.status === 'WAITING_FOR_INSPECTION' || p.status === 'ON_HOLD' || p.status === 'PARTIALLY_ACCEPTED';
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="incoming-inspection">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-sky-500" />
            Incoming Inspection
          </h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Inspection
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by GRN, Item, Result..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No inspections found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>GRN</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">{getPurchaseLabel(item.purchase_id)}</TableCell>
                      <TableCell>{getItemName(item.item_id)}</TableCell>
                      <TableCell>{new Date(item.inspection_date).toLocaleDateString()}</TableCell>
                      <TableCell>{getEmployeeName(item.employee_id)}</TableCell>
                      <TableCell>{getResultBadge(item.inspection_result)}</TableCell>
                      <TableCell>{getStoreName(item.moved_to_location)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Inspection Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Inspection' : 'New Inspection'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div>
                <Label className="form-label">GRN <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.purchase_id}
                  onValueChange={handlePurchaseChange}
                  disabled={!!editingItem}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select GRN" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {(editingItem ? purchases : pendingPurchases).map((purchase) => (
                      <SelectItem key={purchase.id} value={purchase.id.toString()}>
                        GRN-{purchase.grn_no} ({purchase.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Item <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.item_id}
                  onValueChange={function(val) { setFormData(function(prev) { return Object.assign({}, prev, { item_id: val }); }); }}
                  disabled={!formData.purchase_id || !!editingItem}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Item" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {purchaseItems.map(function(item) {
                      var isInspected = !editingItem && inspectedItemIds.indexOf(item.id) !== -1;
                      return (
                        <SelectItem key={item.id} value={item.id.toString()} disabled={isInspected}>
                          {item.code} - {item.name} (Qty: {item.quantity}){isInspected ? ' [Already Inspected]' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {!editingItem && purchaseItems.length > 0 && inspectedItemIds.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{inspectedItemIds.length} of {purchaseItems.length} items already inspected</p>
                )}
              </div>
              <div>
                <Label className="form-label">Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.inspection_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, inspection_date: e.target.value }))}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Inspector <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, employee_id: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Employee" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {employees.filter(e => e.is_active).map((emp) => (
                      <SelectItem key={emp.id} value={emp.id.toString()}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Result <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.inspection_result}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, inspection_result: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Result" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSPECTION_RESULTS.map((result) => (
                      <SelectItem key={result.value} value={result.value}>
                        {result.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Move To Location</Label>
                <Select
                  value={formData.moved_to_location}
                  onValueChange={function(val) { setFormData(function(prev) { return Object.assign({}, prev, { moved_to_location: val }); }); }}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Store" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {stores.filter(function(s) { return s.is_active; }).map(function(store) {
                      return (
                        <SelectItem key={store.id} value={store.name}>{store.name}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Inspection Methods Section */}
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Inspection Methods</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMethod}
                  className="rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Method
                </Button>
              </div>
              
              {methodDetails.length === 0 ? (
                <div className="text-center py-6 text-slate-500 border border-dashed rounded-sm">
                  No inspection methods added. Click "Add Method" to add inspection details.
                </div>
              ) : (
                <div className="space-y-3">
                  {methodDetails.map((detail, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Method</Label>
                        <Select
                          value={detail.method}
                          onValueChange={(val) => handleMethodChange(index, 'method', val)}
                        >
                          <SelectTrigger className="rounded-sm">
                            <SelectValue placeholder="Select Method" />
                          </SelectTrigger>
                          <SelectContent>
                            {INSPECTION_METHODS.map((method) => (
                              <SelectItem key={method.value} value={method.value}>
                                {method.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Value</Label>
                        <Input
                          value={detail.value}
                          onChange={(e) => handleMethodChange(index, 'value', e.target.value)}
                          placeholder="Enter value"
                          className="rounded-sm"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveMethod(index)}
                        className="mt-5 text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <Label className="form-label">Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Any additional notes..."
                className="rounded-sm"
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Inspection Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inspection Details</DialogTitle>
          </DialogHeader>
          {viewingInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-slate-500">GRN</span>
                  <p className="font-medium">{getPurchaseLabel(viewingInspection.purchase_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Item</span>
                  <p className="font-medium">{getItemName(viewingInspection.item_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Date</span>
                  <p className="font-medium">{new Date(viewingInspection.inspection_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Inspector</span>
                  <p className="font-medium">{getEmployeeName(viewingInspection.employee_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Result</span>
                  <p>{getResultBadge(viewingInspection.inspection_result)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Moved To</span>
                  <p className="font-medium">{viewingInspection.moved_to_location || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Inspection Methods ({(viewingInspection.method_details ? viewingInspection.method_details.length : 0) || 0})</h4>
                {(viewingInspection.method_details ? viewingInspection.method_details.length : 0) > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Method</TableHead>
                        <TableHead>Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingInspection.method_details.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{getMethodLabel(detail.method)}</TableCell>
                          <TableCell>{detail.value || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-slate-500">No inspection methods recorded</p>
                )}
              </div>

              {viewingInspection.remarks && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Remarks</h4>
                  <p className="text-slate-600">{viewingInspection.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncomingInspection;
